import api, { handleApiResponse, handleApiError } from './api';

class TicketService {
  constructor() {
    this.userContextCache = null;
    this.userContextExpiry = null;
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  // FIXED: Enhanced getTickets with proper sessionStorage handling
  async getTickets(params = {}) {
    try {
      console.log('Fetching tickets with params:', params);
      
      const response = await api.get('/tickets', { params });
      console.log('Tickets fetch response:', response);
      
      const result = handleApiResponse(response);
      
      // FIXED: Store comprehensive user context from backend
      if (result.data?.user_permissions) {
        this.storeUserContext({
          ...result.data.user_permissions,
          fetched_at: Date.now(),
          source: 'tickets_api'
        });
      }
      
      return result;
    } catch (error) {
      console.error('Get tickets error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to view tickets. Please contact your administrator.');
      } else if (error.response?.status === 401) {
        throw new Error('Please log in to view tickets.');
      }
      
      throw new Error(handleApiError(error));
    }
  }

  // FIXED: Get single ticket with proper context handling
  async getTicket(ticketId) {
    try {
      console.log('Fetching ticket with ID:', ticketId);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.get(`/tickets/${numericId}`);
      console.log('Ticket fetch response:', response);
      
      const result = handleApiResponse(response);
      
      // FIXED: Store user context from ticket detail API  
      if (result.data?.user_context) {
        this.storeUserContext({
          ...result.data.user_context,
          fetched_at: Date.now(),
          source: 'ticket_detail_api'
        });
      }
      
      // Store available actions
      if (result.data?.available_actions) {
        this.storeAvailableActions(ticketId, result.data.available_actions);
      }
      
      return result;
    } catch (error) {
      console.error('Get ticket error details:', {
        ticketId: ticketId,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 404) {
        throw new Error('Ticket not found. It may have been deleted or you may not have access to it.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view this ticket.');
      } else if (error.response?.status === 401) {
        throw new Error('Please log in to view this ticket.');
      }
      
      throw new Error(handleApiError(error));
    }
  }

  // FIXED: Store user context with sessionStorage support
  storeUserContext(context) {
    try {
      // Store in sessionStorage (matching your api.js)
      sessionStorage.setItem('user_context', JSON.stringify(context));
      this.userContextCache = context;
      this.userContextExpiry = Date.now() + this.CACHE_DURATION;
      
      console.log('Stored user context:', context);
    } catch (error) {
      console.error('Error storing user context:', error);
    }
  }

  // FIXED: Get user context with proper sessionStorage priority
  getUserContext() {
    try {
      // Check cache first
      if (this.userContextCache && this.userContextExpiry && Date.now() < this.userContextExpiry) {
        console.log('Using cached user context');
        return this.userContextCache;
      }

      // Try sessionStorage first (matching your api.js)
      let stored = sessionStorage.getItem('user_context');
      if (stored) {
        const context = JSON.parse(stored);
        console.log('Retrieved stored user context from sessionStorage:', context);
        this.userContextCache = context;
        this.userContextExpiry = Date.now() + this.CACHE_DURATION;
        return context;
      }

      // Fallback to localStorage for backward compatibility
      stored = localStorage.getItem('user_context');
      if (stored) {
        const context = JSON.parse(stored);
        console.log('Retrieved stored user context from localStorage (fallback):', context);
        // Migrate to sessionStorage
        sessionStorage.setItem('user_context', stored);
        this.userContextCache = context;
        this.userContextExpiry = Date.now() + this.CACHE_DURATION;
        return context;
      }

      // Final fallback: construct from stored user data (FIXED to use sessionStorage)
      const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      const localUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userData = sessionUser.id ? sessionUser : localUser;
      
      console.log('Using fallback user data:', userData);
      
      if (!userData.id) {
        return {
          access_level: 'none',
          can_view: false,
          is_admin: false,
          is_agent: false
        };
      }

      const fallbackContext = {
        user_id: userData.id,
        user_name: userData.name,
        user_email: userData.email,
        is_agent: userData.is_agent === true || userData.is_agent === 1,
        is_admin: this.hasAdminPermission(userData),
        staff_level: userData.staff_level,
        staff_level_name: userData.staff_level_name || this.getStaffLevelName(userData.staff_level),
        user_type: userData.user_type || (userData.is_agent ? 'agent' : 'customer'),
        primary_role: this.getPrimaryRole(userData),
        role_display_name: this.getRoleDisplayName(userData),
        access_level: this.determineAccessLevel(userData),
        
        // FIXED: Map permissions with staff override
        can_view: this.userHasPermission(userData, 'tickets.view') || 
                  this.userHasPermission(userData, 'tickets.view-all') || 
                  this.userHasPermission(userData, 'tickets.view-own') ||
                  userData.is_agent === true,
        can_comment: this.userHasPermission(userData, 'tickets.comment') || userData.is_agent === true,
        can_update: this.userHasPermission(userData, 'tickets.update') || userData.is_agent === true,
        can_assign: this.userHasPermission(userData, 'tickets.assign'),
        can_escalate: this.userHasPermission(userData, 'tickets.escalate') || userData.is_agent === true,
        can_forward: this.userHasPermission(userData, 'tickets.forward') || userData.is_agent === true,
        can_solve: this.userHasPermission(userData, 'tickets.solve') || userData.is_agent === true,
        can_close: this.userHasPermission(userData, 'tickets.close'),
        can_respond: this.userHasPermission(userData, 'tickets.respond') || userData.is_agent === true,
        can_create_behalf: this.userHasPermission(userData, 'tickets.create-behalf'),
        can_search_customers: this.userHasPermission(userData, 'customers.search'),
        can_contact_customers: this.userHasPermission(userData, 'customers.contact'),
        can_start_working: this.userHasPermission(userData, 'tickets.respond') || userData.is_agent === true,
        
        fallback_generated: true,
        generated_at: Date.now()
      };

      console.log('Generated fallback context:', fallbackContext);
      
      // Store the fallback context
      this.storeUserContext(fallbackContext);
      
      return fallbackContext;
    } catch (error) {
      console.error('Error retrieving user context:', error);
      return {
        access_level: 'none',
        can_view: false,
        is_admin: false,
        is_agent: false,
        error: true
      };
    }
  }

  // Helper methods
  getStaffLevelName(staffLevel) {
    if (!staffLevel) return null;
    
    const levelNames = {
      1: 'Junior Staff',
      2: 'Senior Staff', 
      3: 'Lead Staff'
    };
    
    return levelNames[staffLevel] || `Level ${staffLevel} Staff`;
  }

  hasAdminPermission(userData) {
    return userData.permissions?.includes('tickets.view-all') || 
           userData.roles?.includes('admin') ||
           userData.roles?.some(role => {
             const roleName = typeof role === 'string' ? role : role.name;
             return roleName === 'admin' || roleName === 'super-admin';
           }) ||
           false;
  }

  getPrimaryRole(userData) {
    if (userData.roles && userData.roles.length > 0) {
      const firstRole = userData.roles[0];
      return typeof firstRole === 'string' ? firstRole : firstRole.name;
    }
    
    if (this.hasAdminPermission(userData)) {
      return 'admin';
    } else if (userData.is_agent === true || userData.is_agent === 1) {
      return 'agent';
    } else {
      return 'customer';
    }
  }

  getRoleDisplayName(userData) {
    const primaryRole = this.getPrimaryRole(userData);
    
    switch (primaryRole) {
      case 'admin':
      case 'super-admin':
        return 'Administrator';
      case 'agent':
      case 'support-agent':
      case 'staff':
        if (userData.staff_level) {
          return `Level ${userData.staff_level} ${primaryRole.includes('staff') ? 'Staff' : 'Agent'}`;
        }
        return primaryRole.includes('staff') ? 'Staff Member' : 'Support Agent';
      case 'customer':
        return 'Customer';
      default:
        if (userData.is_agent === true || userData.is_agent === 1) {
          return userData.staff_level ? `Level ${userData.staff_level} Agent` : 'Agent';
        }
        return userData.user_type ? userData.user_type.charAt(0).toUpperCase() + userData.user_type.slice(1) : 'User';
    }
  }

  determineAccessLevel(userData) {
    if (this.hasAdminPermission(userData)) {
      return 'all_tickets';
    } else if (userData.is_agent === true || userData.is_agent === 1) {
      return 'assigned_tickets';
    } else {
      return 'own_tickets';
    }
  }

  userHasPermission(userData, permission) {
    // Check direct permissions array
    if (userData.permissions?.includes(permission)) {
      return true;
    }

    // Check permissions within roles
    if (userData.roles && Array.isArray(userData.roles)) {
      for (const role of userData.roles) {
        const roleObj = typeof role === 'string' ? { name: role } : role;
        if (roleObj.permissions && roleObj.permissions.includes(permission)) {
          return true;
        }
      }
    }

    return false;
  }

  // Store available actions for a ticket (FIXED for sessionStorage)
  storeAvailableActions(ticketId, actions) {
    try {
      const key = `ticket_actions_${ticketId}`;
      sessionStorage.setItem(key, JSON.stringify(actions));
    } catch (error) {
      console.error('Error storing available actions:', error);
    }
  }

  // Get available actions for a ticket (FIXED for sessionStorage priority)
  getAvailableActions(ticketId) {
    try {
      const key = `ticket_actions_${ticketId}`;
      const stored = sessionStorage.getItem(key) || localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error retrieving available actions:', error);
      return [];
    }
  }

  // Check if user has specific permission using user context
  hasPermission(permission) {
    const context = this.getUserContext();
    
    // Check the can_ prefixed permission
    const permissionKey = `can_${permission}`;
    if (context[permissionKey] === true) {
      return true;
    }
    
    // Admin override
    if (context.is_admin === true) {
      return true;
    }
    
    // STAFF OVERRIDE: For essential staff operations
    if (context.is_agent === true) {
      const staffEssentialPermissions = [
        'comment', 'respond', 'solve', 'escalate', 'forward', 'update'
      ];
      
      if (staffEssentialPermissions.includes(permission)) {
        console.log(`Staff override: Allowing ${permission} for staff user`);
        return true;
      }
    }
    
    return false;
  }

  // Check if user can perform action on ticket
  canPerformAction(ticketId, action) {
    const actions = this.getAvailableActions(ticketId);
    return actions.includes(action) || this.hasPermission(action);
  }

  // Access level checks
  shouldShowAllTickets() {
    const context = this.getUserContext();
    return context.access_level === 'all_tickets';
  }

  shouldShowOnlyAssigned() {
    const context = this.getUserContext();
    return context.access_level === 'assigned_tickets';
  }

  shouldShowOnlyOwn() {
    const context = this.getUserContext();
    return context.access_level === 'own_tickets';
  }

  // Page titles
  getPageTitle() {
    const context = this.getUserContext();
    
    switch (context.access_level) {
      case 'all_tickets':
        return 'All Support Tickets';
      case 'assigned_tickets':
        return 'My Assigned Tickets';
      case 'own_tickets':
        return 'My Support Requests';
      default:
        return 'Support Tickets';
    }
  }

  getPageDescription() {
    const context = this.getUserContext();
    
    switch (context.access_level) {
      case 'all_tickets':
        return 'Manage and track all support tickets across the system';
      case 'assigned_tickets':
        return 'Manage tickets assigned to you';
      case 'own_tickets':
        return 'Track and manage your support requests';
      default:
        return 'Support ticket management';
    }
  }

  // UI Helpers
  isAdmin() {
    const context = this.getUserContext();
    return context.is_admin === true;
  }

  isAgent() {
    const context = this.getUserContext();
    return context.is_agent === true;
  }

  canAssignTickets() {
    return this.hasPermission('assign');
  }

  canCreateOnBehalf() {
    return this.hasPermission('create_behalf');
  }

  canEscalateTickets() {
    return this.hasPermission('escalate');
  }

  canForwardTickets() {
    return this.hasPermission('forward');
  }

  canResolveTickets() {
    return this.hasPermission('solve');
  }

  canCloseTickets() {
    return this.hasPermission('close');
  }

  canSearchCustomers() {
    const context = this.getUserContext();
    
    // Admin override
    if (context.is_admin === true) {
      return true;
    }
    
    // Check the specific permission
    if (context.can_search_customers === true) {
      return true;
    }
    
    // Staff override - agents should be able to search customers for on-behalf creation
    if (context.is_agent === true) {
      console.log('Staff override: Allowing customer search for agent');
      return true;
    }
    
    return false;
  }

  getUserRole() {
    const context = this.getUserContext();
    return context.primary_role || 'customer';
  }

  // Clear stored context and permissions (FIXED for sessionStorage)
  clearStoredPermissions() {
    try {
      // Clear sessionStorage
      sessionStorage.removeItem('user_context');
      
      // Clear localStorage for backward compatibility
      localStorage.removeItem('user_context');
      
      // Clear ticket-specific actions from both storages
      const sessionKeys = Object.keys(sessionStorage);
      const localKeys = Object.keys(localStorage);
      
      [...sessionKeys, ...localKeys].forEach(key => {
        if (key.startsWith('ticket_actions_')) {
          sessionStorage.removeItem(key);
          localStorage.removeItem(key);
        }
      });
      
      // Clear cache
      this.userContextCache = null;
      this.userContextExpiry = null;
      
      console.log('Cleared all stored permissions and context');
    } catch (error) {
      console.error('Error clearing stored permissions:', error);
    }
  }

  // Method to refresh user context from backend
  async refreshUserContext() {
    try {
      console.log('Refreshing user context from backend...');
      
      // Clear existing context
      this.clearStoredPermissions();
      
      // Fetch fresh context by making a simple API call
      await this.getTickets({ limit: 1 });
      
      return this.getUserContext();
    } catch (error) {
      console.error('Failed to refresh user context:', error);
      return this.getUserContext();
    }
  }

  // API Methods
  async assignByExpertise(ticketId, assignmentData) {
    try {
      console.log('Assigning ticket by expertise:', ticketId, assignmentData);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.post(`/tickets/${numericId}/assign-by-expertise`, assignmentData);
      console.log('Assign by expertise response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Assign by expertise error:', error);
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to assign tickets.');
      } else if (error.response?.status === 400 && error.response?.data?.code === 'expertise_mismatch') {
        return error.response.data;
      }
      
      throw new Error(handleApiError(error));
    }
  }

  async searchCustomers(searchTerm) {
    try {
      console.log('Searching customers with term:', searchTerm);
      
      if (!searchTerm || searchTerm.trim().length < 2) {
        return { data: { customers: [] } };
      }

      const response = await api.get('/tickets/customers/search', {
        params: { search: searchTerm.trim() }
      });
      console.log('Search customers response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Search customers error:', error);
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to search customers.');
      }
      
      throw new Error(handleApiError(error));
    }
  }

  async createTicketOnBehalf(ticketData) {
    try {
      if (!this.canCreateOnBehalf()) {
        throw new Error('You do not have permission to create tickets on behalf of customers.');
      }

      console.log('Creating ticket on behalf with data:', ticketData);
      
      const response = await api.post('/tickets/on-behalf', ticketData);
      console.log('Create ticket on behalf response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Create ticket on behalf error:', error);
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to create tickets on behalf of customers.');
      }
      
      throw new Error(handleApiError(error));
    }
  }

  async createTicket(ticketData) {
    try {
      console.log('Creating ticket with data:', ticketData);
      
      const response = await api.post('/tickets', ticketData);
      console.log('Create ticket response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Create ticket error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async updateTicket(ticketId, updates) {
    try {
      console.log('Updating ticket:', ticketId, 'with data:', updates);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.patch(`/tickets/${numericId}`, updates);
      console.log('Update ticket response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Update ticket error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async changeStatus(ticketId, status, remarks = '') {
    try {
      console.log('Changing ticket status:', ticketId, 'to:', status);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.patch(`/tickets/${numericId}/status`, {
        status: status,
        remarks: remarks
      });
      console.log('Change status response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Change status error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async addComment(ticketId, content, isPrivate = false, attachments = []) {
    try {
      console.log('Adding comment to ticket:', ticketId, 'with attachments:', attachments.length);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const formData = new FormData();
      formData.append('content', content);
      formData.append('is_private', isPrivate ? '1' : '0');
      
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const response = await api.post(`/tickets/${numericId}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Add comment response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Add comment error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async startWorking(ticketId, notes = '') {
    try {
      console.log('Starting work on ticket:', ticketId);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.patch(`/tickets/${numericId}/start-working`, {
        notes: notes
      });
      console.log('Start working response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Start working error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async getAvailableExperts(ticketId) {
    try {
      console.log('Getting available experts for ticket:', ticketId);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.get(`/tickets/${numericId}/available-experts`);
      console.log('Available experts response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Get available experts error:', error);
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to view ticket experts.');
      }
      
      throw new Error(handleApiError(error));
    }
  }

  async markSolved(ticketId, solutionData) {
    try {
      console.log('Marking ticket as solved:', ticketId, solutionData);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.patch(`/tickets/${numericId}/mark-solved`, solutionData);
      console.log('Mark solved response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Mark solved error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async forwardTicket(ticketId, forwardData) {
    try {
      console.log('Forwarding ticket:', ticketId, forwardData);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      // MAKE SURE this includes is_return_forward
      const response = await api.post(`/tickets/${numericId}/forward`, {
        agent_id: forwardData.agent_id,
        forward_reason: forwardData.forward_reason,
        category_id: forwardData.category_id,
        is_return_forward: forwardData.is_return_forward || false 
      });

      console.log('Forward ticket response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Forward ticket error:', error);
      throw new Error(handleApiError(error));
    }
  }

  // UPDATED: Enhanced escalateTicket method with auto/manual support
  async escalateTicket(ticketId, escalationData) {
    try {
      console.log('Escalating ticket:', ticketId, escalationData);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      // Validate escalation type
      const escalationType = escalationData.escalation_type || 'auto';
      if (!['auto', 'manual'].includes(escalationType)) {
        throw new Error('Invalid escalation type. Must be "auto" or "manual"');
      }

      // Validate manual escalation data
      if (escalationType === 'manual' && !escalationData.agent_id) {
        throw new Error('Agent selection is required for manual escalation');
      }

      const requestData = {
        escalation_reason: escalationData.escalation_reason,
        escalation_type: escalationType,
        urgent: escalationData.urgent || false,
        category_id: escalationData.category_id || null
      };

      // Add agent_id for manual escalation
      if (escalationType === 'manual' && escalationData.agent_id) {
        requestData.agent_id = parseInt(escalationData.agent_id);
      }

      console.log('Sending escalation request:', requestData);

      const response = await api.post(`/tickets/${numericId}/escalate`, requestData);
      console.log('Escalate ticket response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Escalate ticket error:', error);
      
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData?.code === 'agent_required') {
          throw new Error('Agent selection is required for manual escalation');
        } else if (errorData?.code === 'invalid_level') {
          throw new Error('Selected agent must be higher level than current assignment');
        }
      }
      
      throw new Error(handleApiError(error));
    }
  }

  // NEW: Admin reassign escalated ticket method
  async adminReassignEscalated(ticketId, reassignData) {
    try {
      console.log('Admin reassigning escalated ticket:', ticketId, reassignData);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      if (!reassignData.agent_id) {
        throw new Error('Agent selection is required for reassignment');
      }

      if (!reassignData.reassign_reason || !reassignData.reassign_reason.trim()) {
        throw new Error('Reassignment reason is required');
      }

      const requestData = {
        agent_id: parseInt(reassignData.agent_id),
        reassign_reason: reassignData.reassign_reason.trim()
      };

      console.log('Sending admin reassignment request:', requestData);

      const response = await api.post(`/tickets/${numericId}/admin-reassign`, requestData);
      console.log('Admin reassign response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Admin reassign error:', error);
      
      if (error.response?.status === 403) {
        throw new Error('Admin access required for ticket reassignment');
      } else if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData?.code === 'invalid_status') {
          throw new Error('Ticket must be escalated to perform admin reassignment');
        } else if (errorData?.code === 'invalid_agent') {
          throw new Error('Selected user is not an active agent');
        }
      }
      
      throw new Error(handleApiError(error));
    }
  }

  async closeTicket(ticketId) {
    try {
      console.log('Closing ticket:', ticketId);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.patch(`/tickets/${numericId}/close`);
      console.log('Close ticket response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Close ticket error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async getCustomerDetails(ticketId) {
    try {
      console.log('Getting customer details for ticket:', ticketId);
      
      const numericId = parseInt(ticketId);
      if (!ticketId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ticket ID provided');
      }

      const response = await api.get(`/tickets/${numericId}/customer`);
      console.log('Customer details response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Get customer details error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async getCategories() {
    try {
      console.log('Fetching categories');
      
      const response = await api.get('/tickets/categories');
      console.log('Get categories response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Get categories error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async getSubcategories(categoryId) {
    try {
      console.log('Fetching subcategories for category:', categoryId);
      
      const numericId = parseInt(categoryId);
      if (!categoryId || isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid category ID provided');
      }

      const response = await api.get(`/tickets/categories/${numericId}/subcategories`);
      console.log('Get subcategories response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Get subcategories error:', error);
      throw new Error(handleApiError(error));
    }
  }

  async getAgents() {
    try {
      console.log('Fetching agents');
      
      const response = await api.get('/users/agents');
      console.log('Get agents response:', response);
      
      return handleApiResponse(response);
    } catch (error) {
      console.error('Get agents error:', error);
      throw new Error(handleApiError(error));
    }
  }

  // Utility methods
  validateFile(file) {
    const errors = [];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/pdf', 'text/plain', 'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!file) {
      errors.push('No file provided');
      return { isValid: false, errors };
    }

    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File size exceeds maximum allowed size of ${this.formatFileSize(MAX_FILE_SIZE)}`);
    }

    if (file.size === 0) {
      errors.push('File is empty');
    }

    const fileType = file.type?.toLowerCase();
    if (!fileType || !allowedTypes.includes(fileType)) {
      errors.push('File type not supported. Allowed types: images, PDFs, office documents, text files');
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
      fileInfo: {
        name: file.name,
        size: file.size,
        type: file.type
      }
    };
  }

  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  formatStatus(status) {
    const statusMap = {
      'open': { label: 'Open', color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-700' },
      'assigned': { label: 'Assigned', color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
      'in_progress': { label: 'In Progress', color: 'yellow', bgColor: 'bg-yellow-50', textColor: 'text-yellow-700' },
      'pending_customer': { label: 'Pending Customer', color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
      'escalated': { label: 'Escalated', color: 'orange', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
      'resolved': { label: 'Resolved', color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700' },
      'closed': { label: 'Closed', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' }
    };

    return statusMap[status] || {
      label: status || 'Unknown',
      color: 'gray',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700'
    };
  }

  formatPriority(priority) {
    const priorityMap = {
      'low': { label: 'Low', color: 'gray', bgColor: 'bg-gray-50', textColor: 'text-gray-700' },
      'normal': { label: 'Normal', color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
      'medium': { label: 'Medium', color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
      'high': { label: 'High', color: 'orange', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
      'critical': { label: 'Critical', color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-700' }
    };

    return priorityMap[priority] || {
      label: priority || 'Normal',
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    };
  }

  formatTicketId(ticket) {
    if (!ticket || !ticket.id) return 'Unknown';
    return ticket.custom_id || ticket.display_id || `#${ticket.id}`;
  }

  calculateTimeElapsed(dateString) {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      } else if (diffDays < 30) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error('Error calculating time elapsed:', error);
      return 'Unknown';
    }
  }

  calculateResolutionTime(ticket) {
    if (!ticket || ticket.status !== 'resolved' || !ticket.resolved_at || !ticket.created_at) {
      return null;
    }
    
    try {
      const created = new Date(ticket.created_at);
      const resolved = new Date(ticket.resolved_at);
      const diffMs = resolved - created;
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
      } else {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      }
    } catch (error) {
      console.error('Error calculating resolution time:', error);
      return null;
    }
  }

  needsAttention(ticket) {
    if (!ticket) return false;
    
    if (['high', 'critical'].includes(ticket.priority) && 
        ['open', 'assigned'].includes(ticket.status)) {
      return true;
    }
    
    if (this.isOverdue(ticket)) {
      return true;
    }
    
    if (ticket.status === 'escalated') {
      return true;
    }
    
    return false;
  }

  isOverdue(ticket) {
    if (!ticket || !ticket.created_at) return false;
    
    try {
      const created = new Date(ticket.created_at);
      const now = new Date();
      const diffHours = Math.floor((now - created) / (1000 * 60 * 60));
      
      const slaHours = {
        'critical': 2,
        'high': 8,
        'normal': 24,
        'medium': 24,
        'low': 48
      };
      
      const maxHours = slaHours[ticket.priority] || 24;
      
      return diffHours > maxHours && !['resolved', 'closed'].includes(ticket.status);
    } catch (error) {
      console.error('Error checking if ticket is overdue:', error);
      return false;
    }
  }

  getSLAStatus(ticket) {
    if (!ticket || !ticket.created_at || ['resolved', 'closed'].includes(ticket.status)) {
      return 'normal';
    }
    
    try {
      const created = new Date(ticket.created_at);
      const now = new Date();
      const diffHours = Math.floor((now - created) / (1000 * 60 * 60));
      
      const slaHours = {
        'critical': 2,
        'high': 8,
        'normal': 24,
        'medium': 24,
        'low': 48
      };
      
      const maxHours = slaHours[ticket.priority] || 24;
      const warningThreshold = maxHours * 0.75;
      const criticalThreshold = maxHours * 0.9;
      
      if (diffHours > maxHours) {
        return 'overdue';
      } else if (diffHours > criticalThreshold) {
        return 'critical';
      } else if (diffHours > warningThreshold) {
        return 'warning';
      } else {
        return 'normal';
      }
    } catch (error) {
      console.error('Error getting SLA status:', error);
      return 'normal';
    }
  }

  getStatusOptions() {
    return [
      { value: 'open', label: 'Open' },
      { value: 'assigned', label: 'Assigned' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'pending_customer', label: 'Pending Customer' },
      { value: 'escalated', label: 'Escalated' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' }
    ];
  }

  getPriorityLevels() {
    return [
      { value: 'low', label: 'Low' },
      { value: 'normal', label: 'Normal' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'critical', label: 'Critical' }
    ];
  }

  canUserAccessChat(user, ticket) {
    if (!user || !ticket) return false;
    
    if (!user.is_agent) {
      return ticket.creator_id === user.id;
    }
    
    if (user.is_agent) {
      return ticket.agent_id === user.id || ticket.status === 'assigned';
    }
    
    return false;
  }

  isChatEnabled(ticket) {
    if (!ticket) return false;
    
    const enabledStatuses = ['assigned', 'in_progress', 'pending_customer', 'escalated'];
    
    return enabledStatuses.includes(ticket.status) && ticket.agent_id;
  }

  // NEW: Escalation-related helper methods
  getEscalationTypes() {
    return [
      { 
        value: 'auto', 
        label: 'Auto Escalation',
        description: 'System automatically assigns to next level agent'
      },
      { 
        value: 'manual', 
        label: 'Manual Escalation',
        description: 'Select specific higher-level agent'
      }
    ];
  }

  canManuallyEscalate() {
    return this.hasPermission('escalate');
  }

  canAutoEscalate() {
    return this.hasPermission('escalate');
  }

  canAdminReassignEscalated() {
    const context = this.getUserContext();
    return context.is_admin === true;
  }

  // NEW: Enhanced permission checking for escalation features
  getEscalationPermissions() {
    const context = this.getUserContext();
    
    return {
      can_auto_escalate: this.canAutoEscalate(),
      can_manual_escalate: this.canManuallyEscalate(),
      can_admin_reassign: this.canAdminReassignEscalated(),
      can_view_escalation_agents: context.is_agent === true || context.is_admin === true,
      escalation_level: context.staff_level || 1
    };
  }

  // NEW: Validation helpers for escalation
  validateEscalationData(escalationData) {
    const errors = [];
    
    if (!escalationData.escalation_reason || !escalationData.escalation_reason.trim()) {
      errors.push('Escalation reason is required');
    }
    
    if (escalationData.escalation_reason && escalationData.escalation_reason.length > 500) {
      errors.push('Escalation reason must be 500 characters or less');
    }
    
    const validTypes = ['auto', 'manual'];
    if (escalationData.escalation_type && !validTypes.includes(escalationData.escalation_type)) {
      errors.push('Invalid escalation type');
    }
    
    if (escalationData.escalation_type === 'manual' && !escalationData.agent_id) {
      errors.push('Agent selection is required for manual escalation');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  validateAdminReassignData(reassignData) {
    const errors = [];
    
    if (!reassignData.agent_id) {
      errors.push('Agent selection is required');
    }
    
    if (!reassignData.reassign_reason || !reassignData.reassign_reason.trim()) {
      errors.push('Reassignment reason is required');
    }
    
    if (reassignData.reassign_reason && reassignData.reassign_reason.length > 500) {
      errors.push('Reassignment reason must be 500 characters or less');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // NEW: Get escalation status information
  getEscalationStatusInfo(ticket) {
    if (!ticket || ticket.status !== 'escalated') {
      return null;
    }
    
    const context = this.getUserContext();
    
    return {
      is_escalated: true,
      can_admin_reassign: context.is_admin === true,
      current_agent: ticket.agent ? {
        id: ticket.agent.id,
        name: ticket.agent.name,
        staff_level: ticket.agent.staff_level
      } : null,
      escalated_at: ticket.updated_at, // Approximation
      time_since_escalation: this.calculateTimeElapsed(ticket.updated_at)
    };
  }
}

const ticketServiceInstance = new TicketService();
export default ticketServiceInstance;